/**
 * SoleadLab 2.0 — IV Reporting Ticket (Left Rail)
 *
 * Session list + create/delete. Clicking a session selects it
 * and opens the IVPanel floating overlay for detailed work.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useIVStore } from '@/stores/ivStore';
import { useUIStore } from '@/stores/uiStore';
import { useProjectStore } from '@/stores/projectStore';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import type { CreateSessionInput, ImportPayload } from '@/types/iv';
import { parseByLabelMatching, extractSessionMetadataByLabels } from '@/components/iv/ivParser';
import type { IVDeviceAdapter } from '@/components/iv/ivAdapters';
import type { SheetImportTarget } from '@/components/iv/CreateIVSessionModal';
import * as ivApi from '@/services/ivApi';
import CreateIVSessionModal from '@/components/iv/CreateIVSessionModal';
import { useState } from 'react';
import '@/styles/iv.css';

export default function IVReportingTicket() {
  // F-02E-5b: this ticket is hidden from share viewers at the rail level
  // (useShareViewer.isRailItemVisibleForShareViewer → 'iv-reporting' is
  // owner-only; the 'iv' section exposes the IV Results MAP LAYER instead).
  // The isReadOnly guards below stay as defence-in-depth should the ticket
  // ever be mounted for a visitor.
  const isReadOnly = useIsReadOnlyViewer();
  const sessions = useIVStore((s) => s.sessions);
  const selectedSessionId = useIVStore((s) => s.selectedSessionId);
  const loading = useIVStore((s) => s.loading);
  const setSiteId = useIVStore((s) => s.setSiteId);
  const selectSession = useIVStore((s) => s.selectSession);
  const createSession = useIVStore((s) => s.createSession);
  const deleteSession = useIVStore((s) => s.deleteSession);
  const importMeasurements = useIVStore((s) => s.importMeasurements);
  const setPendingTab = useIVStore((s) => s.setPendingTab);

  const siteId = useProjectStore((s) => s.meta.siteId);
  const openPanel = useUIStore((s) => s.openPanel);

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setSiteId(siteId ?? null);
  }, [setSiteId, siteId]);

  const handleSessionClick = (id: string) => {
    selectSession(id);
    openPanel('ivPanel');
  };

  const handleCreate = async (
    input: CreateSessionInput,
    file: File,
    adapter: IVDeviceAdapter | null,
    sheets: SheetImportTarget[],
  ) => {
    const session = await createSession(input);

    // Upload source file to R2
    try {
      await ivApi.uploadSessionSourceFile(session.id, file);
    } catch (err) {
      console.warn('Source file upload failed (non-blocking):', err);
    }

    // Auto-import measurements per sheet if adapter supports label-matching
    let didAutoImport = false;
    if (adapter?.labelMapping && sheets.length > 0) {
      const blockSize = adapter.blockConfig?.blockSize ?? 16;
      const firstRow = adapter.blockConfig?.firstRow ?? 0;
      const fileType = (file.name.split('.').pop()?.toLowerCase() ?? 'xlsx') as 'xlsx' | 'xls' | 'csv';

      // Extract session-level metadata from first sheet only (same across all sheets)
      let fileMetadata: Record<string, string | number> | undefined;
      if (adapter.labelMapping.sessionLabels) {
        try {
          fileMetadata = extractSessionMetadataByLabels(sheets[0].rawRows, blockSize, firstRow, adapter.labelMapping.sessionLabels);
        } catch { /* non-blocking */ }
      }

      for (const sheet of sheets) {
        try {
          const measurements = parseByLabelMatching(sheet.rawRows, blockSize, firstRow, adapter.labelMapping);
          if (measurements.length > 0) {
            const payload: ImportPayload = {
              file_name: file.name,
              file_type: fileType,
              column_mapping: { _mode: 'label', adapter: adapter.id, sheet: sheet.name },
              raw_payload_json: sheet.rawRows.slice(0, 5),
              measurements,
              ...(fileMetadata && !didAutoImport ? { file_metadata: fileMetadata } : {}),
            };
            await importMeasurements(session.id, payload);
            didAutoImport = true;
          }
        } catch (err) {
          console.warn(`Auto-import failed for sheet "${sheet.name}" (non-blocking):`, err);
        }
      }
    }

    await selectSession(session.id);
    openPanel('ivPanel');
    if (didAutoImport) setPendingTab('measurements');
    setShowCreate(false);
  };

  return (
    <div className="iv-ticket">
      {/* F-02E-2C: + New Session is a create mutation; hidden in share viewer. */}
      {!isReadOnly && (
        <div className="iv-ticket__toolbar" data-testid="iv-ticket-toolbar">
          <button
            className="iv-btn iv-btn--primary iv-btn--sm"
            onClick={() => setShowCreate(true)}
          >
            + New Session
          </button>
        </div>
      )}

      <div className="iv-ticket__list">
        {loading && sessions.length === 0 && (
          <div className="iv-ticket__empty">Loading…</div>
        )}
        {!loading && sessions.length === 0 && (
          <div className="iv-ticket__empty">
            {isReadOnly
              ? 'No IV sessions are shared in this view.'
              : <>No sessions yet.<br />Create one to get started.</>}
          </div>
        )}
        {sessions.map((s) => (
          <div
            key={s.id}
            className={`iv-session-card${s.id === selectedSessionId ? ' iv-session-card--active' : ''}`}
            onClick={() => handleSessionClick(s.id)}
          >
            <div className="iv-session-card__name">{s.name}</div>
            <div className="iv-session-card__meta">
              <span className={`iv-badge iv-badge--${s.status}`}>{s.status}</span>
              <span>{s.measurement_scope}</span>
            </div>
            {s.site_name && (
              <div className="iv-session-card__site">{s.site_name}</div>
            )}
            {s.inspection_date && (
              <div className="iv-session-card__date">📅 {s.inspection_date}</div>
            )}
            <div className="iv-session-card__actions">
              <button
                className="iv-btn iv-btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSessionClick(s.id);
                }}
                title="Open details"
              >
                Open
              </button>
              {/* F-02E-2C: per-session Delete hidden in share viewer. */}
              {!isReadOnly && (
                <button
                  className="iv-btn iv-btn--danger iv-btn--sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete "${s.name}"?`)) {
                      deleteSession(s.id);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* F-02E-2C: defence-in-depth — CreateIVSessionModal should be
          unreachable for share viewers (the + New Session entry-point is
          hidden) but if showCreate is somehow flipped via a stale state
          we still suppress the modal mount. */}
      {showCreate && !isReadOnly && createPortal(
        <CreateIVSessionModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />,
        document.body
      )}
    </div>
  );
}
